import { useState } from 'react';
import { Calendar, Users, Bed, RefreshCw } from 'lucide-react';

interface Room {
  room_number: string;
  type: 'Single' | 'Royal' | 'Suite';
  status: 'available' | 'occupied' | 'cleaning' | 'maintenance' | 'out_of_order';
  current_guest?: {
    name: string;
    check_in: string;
    check_out: string;
  } | null;
}

interface RoomOccupancyDashboardProps {
  rooms: Room[];
  onRoomClick?: (room: Room) => void;
}

export function RoomOccupancyDashboard({ rooms, onRoomClick }: RoomOccupancyDashboardProps) {
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);

  // Filter rooms
  const filteredRooms = rooms.filter(room => {
    if (selectedType !== 'all' && room.type !== selectedType) return false;
    return true;
  });

  // Group rooms by type for organized display
  const roomsByType = [
    { type: 'Suite', rooms: filteredRooms.filter(r => r.type === 'Suite') },
    { type: 'Royal', rooms: filteredRooms.filter(r => r.type === 'Royal') },
    { type: 'Single', rooms: filteredRooms.filter(r => r.type === 'Single') },
  ].filter(group => group.rooms.length > 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 border-green-400 text-green-800 hover:bg-green-200';
      case 'occupied':
        return 'bg-red-100 border-red-400 text-red-800 hover:bg-red-200';
      case 'cleaning':
        return 'bg-yellow-100 border-yellow-400 text-yellow-800 hover:bg-yellow-200';
      case 'maintenance':
        return 'bg-gray-100 border-gray-400 text-gray-800 hover:bg-gray-200';
      default:
        return 'bg-gray-100 border-gray-400 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available':
        return '✓';
      case 'occupied':
        return '👤';
      case 'cleaning':
        return '🧹';
      case 'maintenance':
        return '🔧';
      default:
        return '';
    }
  };

  const getRoomTypeColor = (type: string) => {
    switch (type) {
      case 'Suite':
        return 'bg-purple-50 border-purple-300';
      case 'Royal':
        return 'bg-blue-50 border-blue-300';
      case 'Single':
        return 'bg-gray-50 border-gray-300';
      default:
        return 'bg-gray-50 border-gray-300';
    }
  };

  // Statistics
  const stats = {
    total: filteredRooms.length,
    available: filteredRooms.filter(r => r.status === 'available').length,
    occupied: filteredRooms.filter(r => r.status === 'occupied').length,
    cleaning: filteredRooms.filter(r => r.status === 'cleaning').length,
    maintenance: filteredRooms.filter(r => r.status === 'maintenance').length,
  };

  const occupancyRate = stats.total > 0 ? ((stats.occupied / stats.total) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
              <Bed className="w-6 h-6 text-gold-600" />
              Room Occupancy Dashboard
            </h2>
            <p className="text-gray-600 text-sm mt-1">Real-time room availability and status</p>
          </div>
          
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Bed className="w-4 h-4 inline mr-1" />
              Room Type
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="Single">Single Room</option>
              <option value="Royal">Royal Room</option>
              <option value="Suite">Suite Room</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-gray-900 bg-white"
              style={{ colorScheme: 'light' }}
            />
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="text-sm text-gray-600">Total Rooms</div>
          <div className="text-2xl font-bold text-navy-900">{stats.total}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
          <div className="text-sm text-gray-600">Available</div>
          <div className="text-2xl font-bold text-green-600">{stats.available}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-red-500">
          <div className="text-sm text-gray-600">Occupied</div>
          <div className="text-2xl font-bold text-red-600">{stats.occupied}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-yellow-500">
          <div className="text-sm text-gray-600">Cleaning</div>
          <div className="text-2xl font-bold text-yellow-600">{stats.cleaning}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-gold-500">
          <div className="text-sm text-gray-600">Occupancy</div>
          <div className="text-2xl font-bold text-gold-600">{occupancyRate}%</div>
        </div>
      </div>

      {/* Room Grid by Type */}
      <div className="space-y-6">
        {roomsByType.map(({ type, rooms: typeRooms }) => (
          <div key={type} className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold text-navy-900 mb-4 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                type === 'Suite' ? 'bg-purple-100 text-purple-600' :
                type === 'Royal' ? 'bg-blue-100 text-blue-600' :
                'bg-gray-100 text-gray-600'
              }`}>
                {type === 'Suite' ? 'S' : type === 'Royal' ? 'R' : 'SG'}
              </div>
              {type} Rooms
              <span className="text-sm font-normal text-gray-500">
                ({typeRooms.length} rooms)
              </span>
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
              {typeRooms.map((room) => (
                <button
                  key={room.room_number}
                  onClick={() => onRoomClick?.(room)}
                  className={`
                    relative p-4 rounded-lg border-2 transition-all duration-200 transform hover:scale-105
                    ${getStatusColor(room.status)}
                    ${getRoomTypeColor(room.type)}
                  `}
                  title={`${room.type} - ${room.status}${room.current_guest ? `\nGuest: ${room.current_guest.name}` : ''}`}
                >
                  {/* Room Number */}
                  <div className="text-center">
                    <div className="text-lg font-bold">{room.room_number}</div>
                    <div className="text-xs font-medium mt-1">{room.type}</div>
                  </div>

                  {/* Status Icon */}
                  <div className="absolute top-1 right-1 text-lg">
                    {getStatusIcon(room.status)}
                  </div>

                  {/* Guest Indicator */}
                  {room.current_guest && (
                    <div className="absolute bottom-1 left-1">
                      <Users className="w-3 h-3" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-bold text-navy-900 mb-4">Legend</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 bg-green-100 border-2 border-green-400 rounded-lg flex items-center justify-center text-xl">
              ✓
            </div>
            <div>
              <div className="font-medium">Available</div>
              <div className="text-xs text-gray-600">Ready to book</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 bg-red-100 border-2 border-red-400 rounded-lg flex items-center justify-center text-xl">
              👤
            </div>
            <div>
              <div className="font-medium">Occupied</div>
              <div className="text-xs text-gray-600">Guest checked in</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 bg-yellow-100 border-2 border-yellow-400 rounded-lg flex items-center justify-center text-xl">
              🧹
            </div>
            <div>
              <div className="font-medium">Cleaning</div>
              <div className="text-xs text-gray-600">Being cleaned</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 bg-gray-100 border-2 border-gray-400 rounded-lg flex items-center justify-center text-xl">
              🔧
            </div>
            <div>
              <div className="font-medium">Maintenance</div>
              <div className="text-xs text-gray-600">Under repair</div>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="font-medium text-gray-700 mb-3">Room Types</h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gray-50 border-2 border-gray-300 rounded"></div>
              <span className="text-sm">Single Room</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-50 border-2 border-blue-300 rounded"></div>
              <span className="text-sm">Royal Room</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-50 border-2 border-purple-300 rounded"></div>
              <span className="text-sm">Suite Room</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
